import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

function Home() {
	return (
		<>
			<Helmet>
				<title>Appex Connect Hub • Professional Specialty Contracting</title>
				<meta name="description" content="APEX SPECIALTY CONTRACTING LLC delivers professional specialty contracting with clear communications, scheduling updates, and compliant SMS for project coordination." />
				<script type="application/ld+json">{JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'Organization',
					name: 'Appex Connect Hub',
					legalName: 'APEX SPECIALTY CONTRACTING LLC',
					url: 'https://appexconnecthub.com/',
					address: {
						'@type': 'PostalAddress',
						streetAddress: '183 Highland Rd',
						addressLocality: 'Tarpon Springs',
						addressRegion: 'FL',
						postalCode: '34689',
						addressCountry: 'US'
					},
					sameAs: []
				})}</script>
			</Helmet>
			<section className="hero" style={{ position: 'relative', overflow: 'hidden', backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600&auto=format&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
				<div className="blob" />
				<div className="container">
					<div className="hero-grid">
						<div className="hero-content fade-up">
							<p className="helper" style={{ margin: 0 }}>APEX SPECIALTY CONTRACTING LLC</p>
							<h1 style={{ marginTop: 4 }}>Professional Specialty Contracting You Can Trust</h1>
							<p>
								We deliver construction and specialty contracting services with transparent communication, schedule certainty,
								and opt‑in SMS updates so clients always know the next milestone.
							</p>
							<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
								<Link className="btn" to="/contact">Request a Quote</Link>
								<Link className="btn-outline" to="/services">View Services</Link>
								<Link className="btn-outline" to="/compliance">See Compliance</Link>
							</div>
							<p className="helper" style={{ marginTop: 10 }}>
								Msg/data rates may apply. Frequency varies. Reply STOP to opt out, HELP for help.
							</p>
							<div className="badges">
								<span className="badge">Licensed & Insured</span>
								<span className="badge">Schedule Transparency</span>
								<span className="badge">Opt‑in SMS Updates</span>
								<span className="badge">Safety Focused</span>
							</div>
						</div>
						<div className="fade-up" style={{ animationDelay: '120ms' }}>
							<div className="card" style={{ padding: 20 }}>
								<h3 style={{ marginTop: 0 }}>Project Communication</h3>
								<ul className="helper" style={{ marginTop: 8 }}>
									<li>Opt‑in SMS for scheduling, site readiness, and progress updates</li>
									<li>STOP/HELP supported; frequency and rates disclosed</li>
									<li>Audit logs for consent and contact history</li>
									<li>Dedicated support: info@appexconnecthub.com</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="trustbar">
				<div className="container" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
					<p className="helper" style={{ margin: 0 }}>Trusted by lenders and finance teams focused on compliance</p>
					<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
						<span className="pill">Consent‑Driven</span>
						<span className="pill">A2P‑Ready</span>
						<span className="pill">Transparent</span>
					</div>
				</div>
			</section>

			<section className="section">
				<div className="container">
					<h2>Why Appex</h2>
					<div className="grid grid-3 features">
						<div className="card feature value-card fade-up">
							<h3>Construction Focused</h3>
							<p className="helper">Specialty contracting expertise with clear scopes, estimates, and milestone communications.</p>
						</div>
						<div className="card feature value-card fade-up" style={{ animationDelay: '120ms' }}>
							<h3>Schedule Transparency</h3>
							<p className="helper">Upfront timelines, SMS reminders, and prompt updates when conditions change.</p>
						</div>
						<div className="card feature value-card fade-up" style={{ animationDelay: '200ms' }}>
							<h3>Safety & Compliance</h3>
							<p className="helper">Site safety, permitting alignment, and compliant communication (A2P/TCPA).</p>
						</div>
					</div>
				</div>
			</section>

			<section className="section section-alt">
				<div className="container">
					<h2>How We Deliver Projects</h2>
					<div className="process">
						<div className="step fade-up">
							<div className="circle">1</div>
							<h4>Scope & Estimate</h4>
							<p className="helper">On-site or virtual walkthrough, detailed scope, transparent pricing.</p>
						</div>
						<div className="step fade-up" style={{ animationDelay: '100ms' }}>
							<div className="circle">2</div>
							<h4>Schedule & Prep</h4>
							<p className="helper">Timeline confirmation, materials ordering, and opt‑in SMS updates for readiness.</p>
						</div>
						<div className="step fade-up" style={{ animationDelay: '180ms' }}>
							<div className="circle">3</div>
							<h4>Build & Inspect</h4>
							<p className="helper">Field teams execute with safety checks and mid-project status updates.</p>
						</div>
						<div className="step fade-up" style={{ animationDelay: '250ms' }}>
							<div className="circle">4</div>
							<h4>Handoff & Support</h4>
							<p className="helper">Final walkthrough, punch list completion, and responsive post-project support.</p>
						</div>
					</div>
				</div>
			</section>

			<section className="section">
				<div className="container">
					<h2>Communication at the Core</h2>
					<div className="grid grid-3 features">
						<div className="card feature fade-up">
							<h3>Opt‑In SMS Updates</h3>
							<p className="helper">Scheduling, arrival windows, and project updates with STOP/HELP support.</p>
						</div>
						<div className="card feature fade-up" style={{ animationDelay: '100ms' }}>
							<h3>Clear Documentation</h3>
							<p className="helper">Scopes, timelines, and change notices communicated clearly to avoid surprises.</p>
						</div>
						<div className="card feature fade-up" style={{ animationDelay: '180ms' }}>
							<h3>Data Minimization</h3>
							<p className="helper">We collect only what’s needed for scheduling and project coordination.</p>
						</div>
					</div>
				</div>
			</section>

			<section className="section section-alt">
				<div className="container">
					<h2>Project Outcomes</h2>
					<div className="stats">
						<div className="stat card fade-up">
							<strong>98%</strong>
							<p className="helper">On-time arrival windows across recent projects</p>
						</div>
						<div className="stat card fade-up" style={{ animationDelay: '100ms' }}>
							<strong>4.8/5</strong>
							<p className="helper">Average client satisfaction on communication</p>
						</div>
						<div className="stat card fade-up" style={{ animationDelay: '180ms' }}>
							<strong>0</strong>
							<p className="helper">Unresolved STOP requests (honored immediately)</p>
						</div>
					</div>
				</div>
			</section>

			<section className="section">
				<div className="container">
					<h2>What Clients Say</h2>
					<div className="testimonials grid grid-3">
						<div className="card testimonial">
							<p>"They showed up on time, kept us updated, and finished ahead of schedule."</p>
							<p className="helper">— Facilities Manager, Commercial Client</p>
						</div>
						<div className="card testimonial">
							<p>"Clear scope, transparent pricing, and proactive SMS updates—excellent experience."</p>
							<p className="helper">— Homeowner, Renovation Project</p>
						</div>
						<div className="card testimonial">
							<p>"Safety-focused crew and responsive support; we knew exactly what to expect each day."</p>
							<p className="helper">— Operations Lead, Industrial Client</p>
						</div>
					</div>
				</div>
			</section>

			<section className="cta-strip">
				<div className="container" style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
					<div>
						<h3 style={{ margin: 0 }}>Ready to schedule your project?</h3>
						<p className="helper" style={{ margin: 0 }}>Get a transparent quote with opt‑in SMS updates for every milestone.</p>
					</div>
					<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
						<Link className="btn" to="/contact">Request a Quote</Link>
						<Link className="btn-outline" to="/services">Explore Services</Link>
					</div>
				</div>
			</section>

			<section className="section">
				<div className="container two-col">
					<div className="img-frame fade-up">
						<img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1600&auto=format&fit=crop" alt="Contractor reviewing plans on site" />
					</div>
					<div className="fade-up" style={{ animationDelay: '120ms' }}>
						<h2>Craftsmanship & Clarity</h2>
						<p className="helper">
							From pre-construction planning to punch list completion, we keep every stakeholder informed.
							Your timeline, site readiness, and safety priorities stay front and center.
						</p>
						<div className="grid grid-3">
							<div className="card" style={{ padding: 14 }}>
								<h4 style={{ margin: '0 0 6px' }}>Site Prep</h4>
								<p className="helper">Arrival windows, staging, and access confirmed ahead of time.</p>
							</div>
							<div className="card" style={{ padding: 14 }}>
								<h4 style={{ margin: '0 0 6px' }}>In-Progress Updates</h4>
								<p className="helper">Mid-project check-ins via opt-in SMS or email.</p>
							</div>
							<div className="card" style={{ padding: 14 }}>
								<h4 style={{ margin: '0 0 6px' }}>Closeout</h4>
								<p className="helper">Final walkthrough, punch list closure, and documentation.</p>
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="section section-alt">
				<div className="container">
					<h2>Project Highlights</h2>
					<div className="gallery-grid">
						<div className="img-frame fade-up">
							<img src="https://images.unsplash.com/photo-1489515217757-5fd1be406fef?q=80&w=1600&auto=format&fit=crop" alt="Crew working on framed structure" />
						</div>
						<div className="img-frame fade-up" style={{ animationDelay: '80ms' }}>
							<img src="https://images.unsplash.com/photo-1582719478248-51a586f5173d?q=80&w=1600&auto=format&fit=crop" alt="Interior renovation in progress" />
						</div>
						<div className="img-frame fade-up" style={{ animationDelay: '140ms' }}>
							<img src="https://images.unsplash.com/photo-1581090700227-1e37b190418e?q=80&w=1600&auto=format&fit=crop" alt="Exterior facade work with scaffolding" />
						</div>
					</div>
				</div>
			</section>
		</>
	);
}

export default Home;


